import React from "react";

import { Text, type TextProps, Tooltip, type TooltipProps } from "~/ui";

interface TruncatedTextWithTooltipProps {
  text: string;
  /** Single-line ellipsis when true; also applies minWidth: 0 for flex rows. */
  truncate?: boolean;
  /** If true, set native title when Tooltip is not rendered. */
  titleFallback?: boolean;
  /** When true, only render Tooltip if content actually overflows. */
  measureOverflow?: boolean;
  /** Optional explicit tooltip label; defaults to `text` when omitted. */
  tooltipLabel?: string;
  /** Extra props forwarded to Tooltip (label and children are managed internally). */
  tooltipProps?: Omit<TooltipProps, "label" | "children">;
  /** Extra props forwarded to Text (children, truncate, and title are managed internally). */
  textProps?: Omit<TextProps, "children" | "truncate" | "title">;
}

/**
 * Design-system helper for consistent truncation + tooltip behavior.
 * - Applies single-line ellipsis via Mantine Text `truncate`
 * - Adds `min-width: 0` to play nicely inside flex rows
 * - Optionally measures overflow before mounting Tooltip
 * - Uses native `title` when Tooltip is not present for accessibility
 */
export function TruncatedTextWithTooltip({
  text,
  truncate = true,
  titleFallback = false,
  measureOverflow = true,
  tooltipLabel,
  tooltipProps,
  textProps,
}: TruncatedTextWithTooltipProps) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [isOverflowing, setIsOverflowing] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!measureOverflow) {
      setIsOverflowing(false);
      return;
    }
    const el = containerRef.current;
    if (!el) {
      setIsOverflowing(false);
      return;
    }
    const measure = () => {
      const overflow = el.scrollWidth > el.clientWidth;
      setIsOverflowing(overflow);
    };
    measure();
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    }
    return () => {
      if (ro) {
        ro.disconnect();
      }
    };
  }, [measureOverflow, text, truncate]);

  const trimmedTooltip = (tooltipLabel ?? text)?.trim();
  const hasTooltipContent = trimmedTooltip.length > 0;
  const shouldShowTooltip = hasTooltipContent && (!measureOverflow || isOverflowing);

  const title = titleFallback && !shouldShowTooltip && hasTooltipContent ? trimmedTooltip : undefined;

  const style = truncate ? { minWidth: 0, ...(textProps?.style ?? {}) } : textProps?.style;

  const content = (
    <Text ref={containerRef} truncate={truncate ? true : undefined} title={title} {...textProps} style={style}>
      {text}
    </Text>
  );

  const mergedTooltipProps = { withArrow: true, position: "top" as const, openDelay: 100, withinPortal: true, ...(tooltipProps ?? {}) };

  return shouldShowTooltip ? (
    <Tooltip label={trimmedTooltip} {...mergedTooltipProps}>
      {content}
    </Tooltip>
  ) : (
    content
  );
}
